// /frontend/components/newfolder/FCAForm.jsx
import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Grid, TextField, Button,
  Divider, IconButton, Tooltip
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import axios from 'axios';
import { parseMoney } from '../../utils/parseMoney';

export default function FCAForm({ month }) {
  const [, setCenters] = useState([]);
  const [entries, setEntries] = useState([]);
  const [, setLoading] = useState(false);
  // Regel-16-Tor W4 (Nachbesserung 1): Feldfehler je Betragsfeld. Schluessel
  // `"<Kostenstelle>.amount_net"` bzw. `"<Kostenstelle>.manual.<Zeile>"`.
  const [feldFehler, setFeldFehler] = useState({});

  const load = async () => {
    setLoading(true);
    const [c, m] = await Promise.all([
      axios.get('/api/fca/cost_centers'),
      axios.get('/api/fca/costs', { params: { month } })
    ]);
    setCenters(c.data);
    // map month rows into local state keyed by center
    const byId = new Map(m.data.map(r => [r.cost_center_id, r]));
    setEntries(
      c.data.filter(z => z.active).map(z => ({
        cost_center_id: z.id,
        name: z.name,
        default_driver: z.default_driver,
        amount_net: byId.get(z.id)?.amount_net ?? '',
        notes: byId.get(z.id)?.notes ?? '',
        manual_map: byId.get(z.id)?.manual_map ?? []
      }))
    );
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [month]); // load is not memoized; month is the only trigger

  // 🔴 M6-Nachbesserung (Regel-16-Tor W4, Regel 22 „Unknown is never 0"):
  // `parseMoney` liefert `null` fuer UNLESBARES. `?? 0` machte daraus eine echte
  // Null in einem Kosteneintrag. Jetzt sperrt ein nicht lesbarer Text das Senden
  // und meldet sich AM FELD.
  // Ein LEERES Feld bleibt dagegen die `0`, die es hier immer war: die Grundmenge
  // las `Number(e.amount_net || 0)`, und `backend/fca/fca_api.py:102` schreibt
  // `amount_net` ungeprueft in die Spalte — eine leere Zeile heisst hier „keine
  // Kosten in diesem Monat", nicht „unbekannt".
  // Gegenmuster: components/MarketPricingOverview.jsx:728-733 (sperrt + Meldung)
  // und admin/v2/NotificationTriggers.jsx:59-65 (`Number.isFinite`).
  // Lesart des Parsers: DE — ein punktgetrennter Wert mit genau drei
  // Nachkommastellen gilt als Tausenderpunkt (`parseMoney('1.234') === 1234`),
  // weil `inputMode="decimal"` auf deutschen Geraeten das Komma liefert. Regeln:
  // utils/parseMoney.js.
  const betrag = (roh) => (roh === '' || roh == null ? 0 : parseMoney(roh));

  /** Fehler eines Feldes loeschen, sobald der Nutzer darin tippt. */
  const fehlerWeg = (key) => setFeldFehler((f) => {
    if (!f[key]) return f;
    const rest = { ...f };
    delete rest[key];
    return rest;
  });

  /** `false`, wenn ein Feld unlesbar war — dann wurde NICHTS gesendet. */
  const save = async () => {
    const fehler = {};
    const nutzlast = entries.map((e, idx) => {
      const netto = betrag(e.amount_net);
      if (netto === null) fehler[`${idx}.amount_net`] = true;
      let manual;
      if (e.default_driver === 'manual') {
        // 🔴 M6: dieselbe Umschrift fuer den Manual-Split. Die Zeilen halten den
        // Wert als TEXT (sonst verschwindet das Komma waehrend des Tippens — der
        // Zustand traegt die Zahl, und die Anzeige faellt auf „2" zurueck, bevor
        // die „5" ankommt); gesendet wird die Zahl.
        manual = (e.manual_map || []).map((r, i) => {
          const wert = betrag(r.amount_net);
          if (wert === null) fehler[`${idx}.manual.${i}`] = true;
          return { ...r, amount_net: wert };
        });
      }
      return {
        cost_center_id: e.cost_center_id,
        amount_net: netto,
        notes: e.notes,
        manual_map: manual,
      };
    });
    setFeldFehler(fehler);
    if (Object.keys(fehler).length > 0) return false; // Halt — nichts senden
    await axios.post('/api/fca/costs', { month, entries: nutzlast });
    return true;
  };

  const allocate = async () => {
    // 🔴 Ohne die Rueckmeldung liefe „Allocate" auch dann los, wenn `save`
    // wegen eines unlesbaren Betrags gar nichts geschrieben hat.
    if (!(await save())) return;
    await axios.post('/api/fca/allocate', { month });
  };

  const rebuild = async () => {
    await axios.post('/api/fca/rebuild', { month });
  };

  const onChangeAmount = (idx, val) => {
    const copy = [...entries];
    copy[idx].amount_net = val;
    setEntries(copy);
    fehlerWeg(`${idx}.amount_net`);
  };

  return (
    <Paper elevation={1} sx={{ p:2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h6">Monthly Costs — {month}</Typography>
        <Box>
          <Tooltip title="Save costs">
            <IconButton onClick={save}><SyncIcon/></IconButton>
          </Tooltip>
          <Button variant="outlined" onClick={allocate} sx={{ ml:1 }}>Allocate</Button>
          <Button variant="contained" onClick={rebuild} sx={{ ml:1 }}>Rebuild FCA</Button>
        </Box>
      </Box>
      <Divider sx={{ my:2 }}/>
      <Grid container spacing={2}>
        {entries.map((e, idx) => (
          <Grid size={{ xs: 12, md: 6 }} key={e.cost_center_id}>
            <Paper variant="outlined" sx={{ p:2 }}>
              <Typography variant="subtitle1">{e.name}</Typography>
              <Typography variant="caption">Driver: {e.default_driver}</Typography>
              <Box mt={1} display="flex" gap={2}>
                {/* 🔴 M6: `inputMode="decimal"` statt `type="number"` — sonst
                    erscheint auf dem Telefon die BUCHSTABENtastatur. Betrag
                    (Geld) → `decimal`. Hausform:
                    components/po/SendungDialog.jsx:637-639. */}
                <TextField
                  label="Amount (net)"
                  value={e.amount_net}
                  inputProps={{ inputMode: 'decimal' }}
                  onChange={(ev)=>onChangeAmount(idx, ev.target.value)}
                  error={Boolean(feldFehler[`${idx}.amount_net`])}
                  helperText={feldFehler[`${idx}.amount_net`] ? 'Please enter a valid amount.' : undefined}
                  fullWidth
                />
                <TextField
                  label="Notes"
                  value={e.notes}
                  onChange={(ev)=>{
                    const copy=[...entries]; copy[idx].notes=ev.target.value; setEntries(copy);
                  }}
                  fullWidth
                />
              </Box>
              {e.default_driver === 'manual' && (
                <Box mt={2}>
                  <Typography variant="body2" sx={{ mb:1 }}>Manual split (SKU → amount)</Typography>
                  <ManualMapEditor
                    rows={e.manual_map}
                    onChange={(rows)=>{
                      const copy=[...entries]; copy[idx].manual_map=rows; setEntries(copy);
                    }}
                    feldFehler={feldFehler}
                    basis={`${idx}.manual.`}
                    fehlerWeg={fehlerWeg}
                  />
                </Box>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
}

function ManualMapEditor({ rows, onChange, feldFehler = {}, basis = '', fehlerWeg }) {
  // 🔴 M6: der Startwert ist LEER, nicht `0`. Das Feld haelt den Text; eine
  // vorbelegte Null muesste erst geloescht werden, bevor „2,5" hineinpasst.
  const add = () => onChange([...(rows||[]), { sku:'', amount_net:'' }]);
  const upd = (i, key, val) => {
    const next = [...rows]; next[i] = {...next[i], [key]: val}; onChange(next);
    if (key === 'amount_net') fehlerWeg?.(`${basis}${i}`);
  };
  const del = (i) => {
    const next = [...rows]; next.splice(i,1); onChange(next);
  };

  return (
    <Box>
      {(rows||[]).map((r, i)=>(
        <Box key={i} display="flex" gap={1} alignItems="center" sx={{ my:0.5 }}>
          <TextField label="SKU" value={r.sku} onChange={(e)=>upd(i,'sku',e.target.value)} size="small"/>
          {/* 🔴 M6: `inputMode="decimal"` statt `type="number"` (Betrag/Geld). Das
              Feld haelt den TEXT; die Umschrift steht in `save()` — sonst
              verschwindet das Komma waehrend des Tippens (Hausform
              `SendungDialog`: roher Text im Zustand, Umschrift vor dem Senden).
              Hausform des Attributs: components/po/SendungDialog.jsx:637-639. */}
          <TextField label="Amount" inputProps={{ inputMode: 'decimal' }} value={r.amount_net} onChange={(e)=>upd(i,'amount_net',e.target.value)} size="small"
            error={Boolean(feldFehler[`${basis}${i}`])}
            helperText={feldFehler[`${basis}${i}`] ? 'Please enter a valid amount.' : undefined}/>
          <Button size="small" color="error" onClick={()=>del(i)}>Remove</Button>
        </Box>
      ))}
      <Button size="small" variant="outlined" onClick={add}>Add row</Button>
    </Box>
  );
}
